// hooks/useCartCalculations.ts
import { useMemo } from 'react'
import { DeliveryType } from '../../../../common/types/enums'
import { CartItem, DeliveryRule } from '../../../../common/types/types'
import { useCartValidation } from './useCartValidation'
import { useCartData } from './useCartData'

interface UseCartCalculationsProps {
  cartItems: CartItem[]
  deliveryType: DeliveryType
  pickUpStore?: number
  selectedPromo?: any | null
  discountCode: string
  bonusesToSpend?: number
  maxWriteOff?: number
  deliveryFee?: number
  deliveryDistance?: number | null
}

export const useCartCalculations = ({
  cartItems,
  deliveryType,
  pickUpStore,
  selectedPromo,
  discountCode,
  bonusesToSpend = 0,
  maxWriteOff = 0,
  deliveryFee: externalDeliveryFee,
  deliveryDistance,
}: UseCartCalculationsProps) => {
  const { currentCluster } = useCartData()
  const { calculateDiscount, roundMoney, calculateItemsTotal } = useCartValidation()

  const itemsTotal = useMemo((): number => {
    return calculateItemsTotal(cartItems)
  }, [cartItems, calculateItemsTotal])

  const discountAmount = useMemo((): number => {
    if (deliveryType !== DeliveryType.PICKUP || !selectedPromo) return 0

    const storeId = deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined
    return calculateDiscount(
      itemsTotal,
      selectedPromo,
      cartItems,
      storeId,
      discountCode,
      deliveryType,
    )
  }, [
    itemsTotal,
    selectedPromo,
    cartItems,
    pickUpStore,
    discountCode,
    deliveryType,
    calculateDiscount,
  ])

  const deliveryFee = useMemo((): number => {
    if (deliveryType !== DeliveryType.DELIVERY) return 0
    if (externalDeliveryFee !== undefined) return externalDeliveryFee
    return 0
  }, [deliveryType, externalDeliveryFee])

  // Все правила доставки, подходящие по расстоянию
  const applicableRules = useMemo((): DeliveryRule[] => {
    if (!currentCluster?.delivery?.length || !deliveryDistance) return []

    return currentCluster.delivery
      .filter((rule: DeliveryRule) => Number(rule.radius) >= deliveryDistance)
      .sort((a: DeliveryRule, b: DeliveryRule) => Number(a.level) - Number(b.level))
  }, [currentCluster, deliveryDistance])

  // Текущее лучшее условие (для достигнутой суммы)
  const currentBestRule = useMemo((): DeliveryRule | null => {
    if (!applicableRules.length) return null

    const achievedRules = applicableRules.filter(
      (rule: DeliveryRule) => Number(rule.level) <= itemsTotal,
    )
    if (!achievedRules.length) return null

    return achievedRules.reduce((best: DeliveryRule, current: DeliveryRule) =>
      Number(current.value) < Number(best.value) ? current : best,
    )
  }, [applicableRules, itemsTotal])

  // Следующее лучшее условие (ближайший порог с улучшением)
  const nextBestRule = useMemo((): DeliveryRule | null => {
    if (!applicableRules.length) return null

    const futureRules = applicableRules.filter(
      (rule: DeliveryRule) => Number(rule.level) > itemsTotal,
    )
    if (!futureRules.length) return null

    return futureRules[0]
  }, [applicableRules, itemsTotal])

  // Идеальное условие (лучшая цена среди всех подходящих по радиусу)
  const bestPossibleRule = useMemo((): DeliveryRule | null => {
    if (!applicableRules.length) return null

    const rulesByRadius = new Map<number, DeliveryRule[]>()
    applicableRules.forEach((rule: DeliveryRule) => {
      if (!rulesByRadius.has(rule.radius)) {
        rulesByRadius.set(rule.radius, [])
      }
      rulesByRadius.get(rule.radius)!.push(rule)
    })

    const bestByRadius: DeliveryRule[] = []
    rulesByRadius.forEach((rules: DeliveryRule[]) => {
      const bestForRadius = rules.reduce((best: DeliveryRule, current: DeliveryRule) =>
        Number(current.value) < Number(best.value) ? current : best,
      )
      bestByRadius.push(bestForRadius)
    })

    return bestByRadius.reduce((best: DeliveryRule, current: DeliveryRule) =>
      Number(current.value) < Number(best.value) ? current : best,
    )
  }, [applicableRules])

  const actualBonusesToSpend = useMemo((): number => {
    if (bonusesToSpend > maxWriteOff) return maxWriteOff
    if (bonusesToSpend < 0) return 0
    return bonusesToSpend
  }, [bonusesToSpend, maxWriteOff])

  const subtotalWithDiscount = useMemo((): number => {
    return itemsTotal + deliveryFee - discountAmount
  }, [itemsTotal, deliveryFee, discountAmount])

  const totalWithDelivery = useMemo((): number => {
    const total = subtotalWithDiscount - actualBonusesToSpend
    return roundMoney(Math.max(total, 0))
  }, [subtotalWithDiscount, actualBonusesToSpend, roundMoney])

  return {
    itemsTotal,
    deliveryFee,
    discountAmount,
    subtotalWithDiscount,
    totalWithDelivery,
    actualBonusesToSpend,
    // 👇 Убрали nextDeliveryRule
    currentBestRule,
    nextBestRule,
    bestPossibleRule,
    applicableRules,
  }
}
